import { LexicalEditor } from 'lexical';
import { ActionType } from '../components/ActionType';
import { KeyValueMap } from '../KeyValueMap';

export type LexicalToolbarProps = {
    editor: LexicalEditor;
    disabled?: boolean;
    isSmall?: boolean;
};

export const fontFamilyKey = 'font-family';
export const fontSizeKey = 'font-size';
export const MATERIAL_REFERENCE_ATTRIBUTE = 'data-showrunnr-attachment-reference';
export const MATERIAL_REFERENCE_ID_ATTRIBUTE = 'data-reference-id';
export const MATERIAL_REFERENCE_TYPE_ATTRIBUTE = 'data-reference-type';
export const MATERIAL_REFERENCE_LABEL_ATTRIBUTE = 'data-label';
export const MATERIAL_REFERENCE_MIME_TYPE_ATTRIBUTE = 'data-mime-type';
export const MATERIAL_REFERENCE_CLASS = 'showrunnr-attachment-reference';
export const MATERIAL_REFERENCE_LABEL_CLASS = 'showrunnr-attachment-reference__label';
export const MATERIAL_FILE_UPLOADED_ATTRIBUTE = 'data-showrunnr-attachment-uploaded';
export const SPACE_KEYS = new Set([' ', 'Space', 'Spacebar']);

export const COLOR_PICKER_MODES = {
    highlight: 'highlight',
    text: 'text',
    backgroundColor: 'background-color'
};

export const TEXT_TRANSFORM_OPTIONS = {
    uppercase: 'uppercase',
    lowercase: 'lowercase',
    capitalize: 'capitalize',
    subscript: 'subscript',
    superscript: 'superscript',
    clear: 'clear',
    sub: 'sub',
    super: 'super'
};

export const lexicalHighlightColors = [
    { key: 'red', color: '#d0021b' },
    { key: 'orange', color: '#f5a623' },
    { key: 'yellow', color: '#f8e71c' },
    { key: 'brown', color: '#8b572a' },
    { key: 'lime', color: '#7ed321' },
    { key: 'green', color: '#417505' },
    { key: 'magenta', color: '#bd10e0' },
    { key: 'violet', color: '#9013fe' },
    { key: 'blue', color: '#4a90e2' },
    { key: 'cyan', color: '#50e3c2' },
    { key: 'light-green', color: '#b8e986' },
    { key: 'black', color: '#000000' },
    { key: 'dark-gray', color: '#4a4a4a' },
    { key: 'gray', color: '#9b9b9b' },
    { key: 'white', color: '#fff' }
];

export const LEXICAL_LAYOUT_TYPES = {
    table: 'table',
    tableLayout: 'tableLayout',
    columns: 'columns',
    columnsLayout: 'columnsLayout'
};

/**
 * Defines the available column layout options for a layout builder or grid system.
 * Each layout option includes:
 * - `id`: For layout.
 * - `label`: A label describing the layout.
 * - `value`: The internal value representing the layout configuration.
 */

export const COLUMN_LAYOUTS = [
    { id: '2-equal', label: '2 columns (equal width)', value: '2-equal' },
    { id: '2-25-75', label: '2 columns (25% - 75%)', value: '2-25-75' },
    { id: '3-equal', label: '3 columns (equal width)', value: '3-equal' },
    { id: '3-25-50-25', label: '3 columns (25% 50% 25%)', value: '3-25-50-25' },
    { id: '4-equal', label: '4 columns (equal width)', value: '4-equal' }
];

export const LAYOUT_MAP = {
    '2-equal': ['1fr', '1fr'],
    '2-25-75': ['1fr', '3fr'],
    '3-equal': ['1fr', '1fr', '1fr'],
    '3-25-50-25': ['1fr', '2fr', '1fr'],
    '4-equal': ['1fr', '1fr', '1fr', '1fr']
};

export const FONT_FAMILIES: KeyValueMap<string, ActionType> = {
    Arial: { label: 'lexical.font.arial' },
    'Courier New': { label: 'lexical.font.courier.new' },
    Georgia: { label: 'lexical.font.georgia' },
    'Times New Roman': { label: 'lexical.font.times.new.roman' },
    'Trebuchet MS': { label: 'lexical.font.trebuchet.ms' },
    Verdana: { label: 'lexical.font.verdana' }
};

// Regex to detect black or white colors. It handles rgb, rgba, hex, and named colors.
// For rgba, it checks for black/white regardless of the alpha value.
export const isBlackOrWhiteRegex = new RegExp(
    'color:\\s*(' +
    [
        // Black
        'rgb\\(\\s*0\\s*,\\s*0\\s*,\\s*0\\s*\\)',
        'rgba\\(\\s*0\\s*,\\s*0\\s*,\\s*0\\s*,\\s*[\\d.]+\\s*\\)',
        '#000(000)?',
        'black',
        'hsl\\(\\s*0\\s*,\\s*0%\\s*,\\s*0%\\s*\\)',
        'hsla\\(\\s*0\\s*,\\s*0%\\s*,\\s*0%\\s*,\\s*[\\d.]+\\s*\\)',

        // White
        'rgb\\(\\s*255\\s*,\\s*255\\s*,\\s*255\\s*\\)',
        'rgba\\(\\s*255\\s*,\\s*255\\s*,\\s*255\\s*,\\s*[\\d.]+\\s*\\)',
        '#fff(fff)?',
        'white',
        'hsl\\(\\s*0\\s*,\\s*0%\\s*,\\s*100%\\s*\\)',
        'hsla\\(\\s*0\\s*,\\s*0%\\s*,\\s*100%\\s*,\\s*[\\d.]+\\s*\\)'
    ].join('|') +
    ')(?=[;]|$)',
    'i'
);
